import type { ReactNode } from "react";
import { Icon } from "@core/icons";
import { tokens } from "@core/theme/tokens";
import { portalPrimitiveValues as values } from "./portalPrimitiveValues";

/**
 * Casilla de verificación con el look "chip" del sistema (revisión en vivo
 * 2026-09-22, punto #4B). Hoy el portal dibuja siete `<input type="checkbox">`
 * nativos en seis archivos ("Enviar invitación al guardar", "Activo" de
 * catálogos, filtros de Miembros, lugares habilitados, entrega del premio):
 * todos se renderizan con el control del sistema operativo, que no se parece a
 * nada del resto de la interfaz.
 *
 * El `<input>` real sigue ahí, oculto a la vista pero no a la accesibilidad:
 * el rol, el `checked`, el foco, la barra espaciadora y la asociación con la
 * etiqueta son los nativos — nada de `role="checkbox"` simulado. Por eso el
 * anillo de foco se dibuja en la etiqueta (`.portal-checkbox:focus-within`,
 * base.css): en un `style` inline no se puede expresar `:focus-within`.
 *
 * El estado marcado usa el par neutro del sistema (`surface3` + `line2`), el
 * mismo de `SegmentedControl`, nunca el champagne: ese acento es exclusivo de
 * recompensas y logros (DESIGN-TOKENS.md §1.4).
 */
export function Checkbox({
  checked,
  onChange,
  label,
  ariaLabel,
  disabled = false,
  name,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  // Sin `label` visible (selección de fila de una tabla) el control se dibuja
  // "desnudo": solo la caja, sin el chip. El chip mide 53×36 y no entra en la
  // columna de 30px de la tabla de Miembros — ahí el chip no es el look
  // correcto, la caja sí. En ese caso el nombre accesible viene de
  // `ariaLabel`, que es obligatorio: un checkbox sin nombre no es usable.
  label?: ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  name?: string;
}) {
  const bare = label === undefined;
  return (
    <label
      className="portal-checkbox"
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? values.checkbox.disabledOpacity : 1,
        transition: "all 0.18s ease",
        ...(bare
          ? { borderRadius: values.checkbox.boxRadius }
          : {
              gap: values.checkbox.gap,
              padding: values.checkbox.padding,
              borderRadius: values.checkbox.radius,
              border: `1px solid ${checked ? tokens.surface.line2 : tokens.surface.line}`,
              background: checked
                ? tokens.surface.surface3
                : tokens.surface.surface,
              color: checked ? tokens.text.primary : tokens.text.secondary,
              font: values.checkbox.font,
              letterSpacing: "0.01em",
            }),
      }}
    >
      <input
        type="checkbox"
        name={name}
        checked={checked}
        disabled={disabled}
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
        onChange={(event) => onChange(event.target.checked)}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          margin: 0,
          padding: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
      />
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
          width: values.checkbox.boxSize,
          height: values.checkbox.boxSize,
          borderRadius: values.checkbox.boxRadius,
          border: `1px solid ${checked ? tokens.text.primary : tokens.surface.line2}`,
          background: checked ? tokens.text.primary : "transparent",
          color: tokens.surface.bg2,
        }}
      >
        {checked ? (
          <Icon name="check" size={values.checkbox.checkSize} />
        ) : null}
      </span>
      {bare ? null : <span>{label}</span>}
    </label>
  );
}
